import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import { TIER_DISPLAY_NAMES, TIER_MAX_PROJECTS } from '@/atoms/subscription.atom';
import { userThemeAtom } from '@/atoms/user.atom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { useMe } from '@/hooks/useMe';
import { useOrganizations } from '@/hooks/useOrganizations';
import { analytics } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import ProjectCreateModal from '@/modals/ProjectCreateModel';
import TrialWelcomeModal from '@/modals/TrialWelcomeModal';
import { useDialog } from '@/providers/DialogProvider';
import { organizationUsersQuery } from '@/queries/organization.query';
import { archivedProjectsQuery, myProjectsQuery } from '@/queries/project.query';
import {
  createProjectFolderMutation,
  moveProjectToFolderMutation,
  projectFoldersQuery,
} from '@/queries/projectFolder.query';
import { selectOnboardingProject } from '../onboarding/selectOnboardingProject';
import {
  Archive,
  CogFourSolid,
  CreditCard,
  Desktop,
  FileText,
  Home,
  LinkOne,
  LocationSelected,
  Logout,
  Map,
  Moon,
  Plus,
  Sun,
  Support,
  User,
  Users,
} from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { FolderPlus, Gift, Sparkles } from 'lucide-react';
import prettyBytes from 'pretty-bytes';
import { FunctionComponent, JSX, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import Organizations from '../Organizations';
import PortalTooltip from '../PortalTooltip';
import { Group } from './Group';
import { Label } from './Label';
import { ProjectList } from './ProjectList';
import { Section } from './Section';
import { Suboption } from './Suboption';

const formatBytes = (bytes: number | null | undefined) => {
  if (!bytes) return '0 B';
  return prettyBytes(bytes);
};

const getDocumentationUrl = () => {
  return 'https://help.skygis.cloud';
};

export const useIsActive = (href?: string, isPrefix: boolean = false) => {
  const location = useLocation();

  const isActive = useMemo(() => {
    if (!href) return false;

    return isPrefix ? location.pathname.startsWith(href) : location.pathname === href;
    // const hrefRegex = `${href.replace(/\//g, '\\/')}(\\/.*)?`;
    // return new RegExp(`^${hrefRegex}$`).test(location.pathname);
  }, [href, isPrefix, location.pathname]);

  return isActive;
};

interface iOption {
  label: string;
  icon?: React.ReactNode;
  href?: string;
  disabled?: boolean;
  badge?: string;
  suboptions?: { label: string; href?: string; icon?: React.ReactNode }[];
}

const OPTIONS: Record<string, Record<string, iOption>> = {} as const;

export const usePages = () => {
  const options = useMemo((): Record<string, Record<string, iOption>> => {
    // Filter options based on user scopes
    const filteredOptions = Object.keys(OPTIONS).reduce(
      (acc, key) => {
        const option = OPTIONS[key];

        const filteredOption = Object.keys(option).reduce(
          (acc, key) => {
            acc[key] = option[key];
            return acc;
          },
          {} as Record<string, iOption>,
        );

        if (Object.keys(filteredOption).length > 0) acc[key] = filteredOption;
        return acc;
      },
      {} as Record<string, Record<string, iOption>>,
    );

    return filteredOptions;
  }, []);

  return options;
};

interface iSidebarProps {}

const Sidebar: FunctionComponent<iSidebarProps> = ({}) => {
  const { t } = useTranslation('sidebar');
  const [theme, setTheme] = useAtom(userThemeAtom);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const { me: user } = useMe();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const [isCollapsed] = useAtom(sidebarCollapsedAtom);
  const { hasRole, organization } = useOrganizations();
  const canReadProjects = hasRole('MEMBER');
  const canManageBilling = hasRole('BILLING_MANAGER');
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['projects', organization?.id],
    queryFn: () => execute(myProjectsQuery),
    enabled: !!organization?.id && canReadProjects,
  });
  const { data: archivedData } = useQuery({
    queryKey: ['archivedProjects', organization?.id],
    queryFn: () => execute(archivedProjectsQuery),
    enabled: !!organization?.id && canReadProjects,
  });
  const { data: foldersData } = useQuery({
    queryKey: ['projectFolders', organization?.id],
    queryFn: () => execute(projectFoldersQuery),
    enabled: !!organization?.id && canReadProjects,
  });

  const _handleCreateProject = (folderId?: string) => {
    if (!organization?.id) return;
    analytics.capture('Project Create Clicked');
    openDialog(
      <ProjectCreateModal
        onSuccess={(projectId?: string) => {
          refetch();
          if (folderId && projectId) {
            execute(moveProjectToFolderMutation, { projectId, folderId }).then(() => {
              queryClient.invalidateQueries({ queryKey: ['projects', organization.id] });
            });
          }
        }}
        organizationId={organization.id}
      />,
    );
  };

  const _handleCreateFolder = () => {
    if (!organization?.id) return;
    execute(createProjectFolderMutation, { name: t('folders.newFolder') }).then(() => {
      queryClient.invalidateQueries({ queryKey: ['projectFolders', organization.id] });
    });
  };

  const _handleLogout = () => {
    navigate('/auth/logout', { replace: true });
  };

  const _handleAccount = () => {
    navigate('/account');
  };

  const isTrialing = organization?.subscription?.billingStatus === 'TRIALING';
  const trialEndsAt = organization?.subscription?.trialEndsAt;
  const trialDaysLeft = trialEndsAt ? Math.max(0, dayjs(trialEndsAt).diff(dayjs(), 'day')) : 0;

  useEffect(() => {
    if (!isTrialing || !organization?.id) return;
    const key = `trial-welcome-shown-${organization.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    openDialog(<TrialWelcomeModal />);
  }, [isTrialing, organization?.id]);

  const projects = data?.projects || [];
  const archivedProjects = archivedData?.archivedProjects || [];
  const onboardingProjectId = selectOnboardingProject(projects, foldersData?.projectFolders ?? [])?.id ?? null;

  const options = usePages();
  return (
    <div
      className={cn(
        'flex z-20 flex-col gap-1 sticky top-0 whitespace-nowrap overflow-auto w-64 border-r border-slate-200 h-full max-h-screen flex-shrink-0 bg-slate-100/70 dark:bg-stone-950 dark:border-stone-800',
        isCollapsed && 'w-12 overflow-hidden',
      )}
    >
      <Organizations />
      <div className='overflow-auto overflow-x-hidden flex-1 no-scrollbar flex flex-col flex-1'>
        <Section>
          {canReadProjects && <Group label={t('nav.dashboard')} icon={<Home />} href='/' />}

          {canReadProjects && (
            <Group
              id='onboarding-project-list'
              loading={isLoading}
              label={t('nav.projects')}
              persistanceKey='projects'
              icon={<LocationSelected />}
              padding={projects.length > 0}
              actionElement={
                hasRole('EDITOR') ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className='text-slate-500/70 dark:text-stone-100 p-1 rounded-sm hover:bg-slate-300/25 active:bg-slate-300/50 [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2 [&>svg]:text-slate-500 dark:[&>svg]:text-stone-100'>
                        <Plus />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side='right' align='start'>
                      <DropdownMenuItem onClick={() => _handleCreateProject()} className='gap-2 text-xs'>
                        <Plus className='size-3.5' />
                        {t('projects.createProject')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={_handleCreateFolder} className='gap-2 text-xs'>
                        <FolderPlus className='size-3.5' />
                        {t('folders.createFolder')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : undefined
              }
            >
              {projects.length > 0 && organization?.id && (
                <ProjectList
                  projects={projects}
                  organizationId={organization.id}
                  onCreateProject={_handleCreateProject}
                  onboardingProjectId={onboardingProjectId}
                />
              )}
              {data?.projects?.length === 0 && !isLoading && (
                <div className='border border-slate-200/70 flex flex-col items-center bg-slate-200/70 dark:bg-stone-800/70 dark:border-stone-800/70 rounded-md font-medium py-4 text-sm mb-1'>
                  <Map className='stroke-2 text-slate-400 size-6 mb-2 dark:text-stone-400' />
                  <span className='text-slate-500 dark:text-stone-200'>{t('projects.noProjects')}</span>
                  <p className='text-slate-500/70 dark:text-stone-200/70 text-xs'>{t('projects.createToGetStarted')}</p>
                </div>
              )}
            </Group>
          )}
          {canReadProjects && (
            <>
              <Group
                label={t('nav.archivedProjects')}
                icon={<Archive />}
                href='/archived-projects'
                persistanceKey='archived-projects'
              />
              <Group
                badge={t('badge.soon')}
                disabled
                label={t('nav.integrations')}
                icon={<LinkOne />}
                href='/integrations'
              />
            </>
          )}

          {hasRole('ADMIN') && (
            <>
              <Group
                label={t('nav.users')}
                icon={<Users />}
                href='/users'
                onPrefetch={() => {
                  queryClient.prefetchQuery({
                    queryKey: ['users', organization?.id],
                    queryFn: () => execute(organizationUsersQuery),
                    staleTime: 30_000,
                  });
                }}
              />
              <Group label={t('nav.subscription')} icon={<CreditCard />} href='/subscription' />
              <Group label={t('nav.settings')} icon={<CogFourSolid />} href='/settings' />
              <div className='flex-1' />
              <Group
                id='onboarding-docs'
                label={t('nav.documentation')}
                icon={<FileText />}
                href={getDocumentationUrl()}
              />
              <Group
                label={t('nav.help')}
                icon={<Support />}
                onClick={() => {
                  // @ts-ignore
                  window.Featurebase?.('show');
                }}
              />
            </>
          )}
          {canManageBilling && !hasRole('ADMIN') && (
            <Group label={t('nav.subscription')} icon={<CreditCard />} href='/subscription' />
          )}
        </Section>
        {Object.keys(options).map((key) => {
          const option = options[key];
          return (
            <Section key={key} isCollapsed={isCollapsed}>
              {key && <Label isCollapsed={isCollapsed}>{key}</Label>}
              {Object.keys(option).map((key) => {
                return (
                  <Group key={key} {...option[key]} isCollapsed={isCollapsed}>
                    {option[key].suboptions?.map((suboption) => {
                      return <Suboption key={suboption.label} label={suboption.label} href={suboption.href} />;
                    })}
                  </Group>
                );
              })}
            </Section>
          );
        })}
      </div>
      {isTrialing && !isCollapsed && (
        <div
          onClick={() => navigate('/subscription')}
          className='mx-2 cursor-pointer rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 px-2.5 py-2 flex items-center gap-2'
        >
          <Sparkles className='size-4 text-blue-500 shrink-0' />
          <div className='flex-1 min-w-0'>
            <p className='text-xs font-semibold text-blue-700 dark:text-blue-300'>
              {t('trial.coreTrial', { tier: organization.subscription?.tier })}
            </p>
            <p className='text-[11px] text-blue-600/80 dark:text-blue-400/80'>
              {t('trial.daysRemaining', { count: trialDaysLeft })}
            </p>
          </div>
        </div>
      )}
      {hasRole('ADMIN') && !isCollapsed && organization?.subscription?.tier !== 'FREE' && (
        <div className='border rounded-md font-medium border-slate-200/70 dark:border-stone-800/70 px-2 py-1.5 mx-2 bg-slate-100 dark:bg-stone-900 flex flex-col gap-1'>
          <div className='flex items-center text-xs gap-2'>
            <span className='flex-1'>{t('subscription.plan')}</span>
            <span className='text-slate-600 dark:text-stone-400'>
              {TIER_DISPLAY_NAMES[organization?.subscription?.tier || 'FREE']}
            </span>
          </div>
          <div className='flex items-center text-xs gap-2'>
            <span className='flex-1'>{t('subscription.storage')}</span>
            <span className='text-slate-600 dark:text-stone-400'>
              {formatBytes(organization?.storageUsedBytes)} / {formatBytes(organization?.storageLimitBytes)}
            </span>
            <ProgressBar
              tooltip={
                <div className='max-w-xs'>
                  <div>
                    {t('subscription.storageTooltip', {
                      used: formatBytes(organization?.storageUsedBytes),
                      total: formatBytes(organization?.storageLimitBytes),
                    })}
                  </div>
                  <div className='text-[11px] opacity-70 mt-0.5'>{t('subscription.storageTooltipArchiveNote')}</div>
                  {(organization as any)?.ephemeralBytes > 0 && (
                    <div className='text-[11px] opacity-70 mt-0.5 italic'>
                      {t('subscription.storageTooltipEphemeralNote', {
                        size: formatBytes((organization as any).ephemeralBytes),
                      })}
                    </div>
                  )}
                  <ul className='text-xs list-disc list-inside mt-1 mb-0.5'>
                    {[
                      ...projects.map((p) => ({ ...p, archived: false })),
                      ...archivedProjects.map((p) => ({ ...p, archived: true })),
                    ]
                      .filter((p) => p.storageUsedBytes > 0)
                      .sort((a, b) => b.storageUsedBytes - a.storageUsedBytes)
                      .map((project) => {
                        const inRetention =
                          !project.archived &&
                          'retentionUntil' in project &&
                          project.retentionUntil &&
                          new Date(project.retentionUntil) > new Date();
                        const suffix = project.archived
                          ? ` ${t('subscription.archivedSuffix')}`
                          : inRetention
                            ? ` ${t('subscription.inRetentionSuffix')}`
                            : '';
                        return (
                          <li key={project.id} className='flex items-center justify-between'>
                            <span
                              className={cn('flex-1', project.archived && 'italic opacity-70', inRetention && 'italic')}
                            >
                              {project.name}
                              {suffix}
                            </span>
                            <span className='text-right font-medium font-mono'>
                              {formatBytes(project.storageUsedBytes)}
                            </span>
                            <span className='text-right font-medium font-mono w-12'>
                              {Math.round((10000 * project.storageUsedBytes) / (organization?.storageLimitBytes || 0)) /
                                100}
                              %
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              }
              value={organization?.storageUsedBytes || 0}
              max={organization?.storageLimitBytes || 0}
            />
          </div>
          {(() => {
            const tier = organization?.subscription?.tier || 'FREE';
            const maxProjects = TIER_MAX_PROJECTS[tier];
            const isUnlimited = maxProjects === null;
            return (
              <div className='flex items-center text-xs gap-2 '>
                <span className='flex-1'>{t('subscription.projects')}</span>
                <span className='text-slate-600 dark:text-stone-400'>
                  {projects.length} {isUnlimited ? '' : `/ ${maxProjects}`}
                </span>
                {!isUnlimited && (
                  <ProgressBar
                    tooltip={t('subscription.projectsTooltip', {
                      count: projects.length,
                      percent: '{{percent}}',
                      max: maxProjects,
                    })}
                    value={projects.length}
                    max={maxProjects}
                  />
                )}
              </div>
            );
          })()}
        </div>
      )}
      <div className='relative'>
        {isUserOpen && <div className='fixed inset-0 z-50' onClick={() => setIsUserOpen(false)} />}
        <div
          className={cn(
            'absolute shadow-md/3 left-2 right-2 z-50 rounded-lg overflow-hidden border border-slate-200/70 bottom-full bg-white dark:bg-stone-900 dark:border-stone-800 transition-all translate-y-2 opacity-0 invisible',
            isCollapsed && 'fixed w-48 left-[unset] right-[unset] bottom-12',
            isUserOpen && 'translate-y-0 opacity-100 visible',
          )}
        >
          <div className='flex px-2.5 py-2.5 gap-2 items-center bg-slate-50/70 border-b border-slate-200/70 dark:bg-stone-800 dark:border-stone-800'>
            <img
              className='size-8 object-cover rounded-md border border-slate-200/70 dark:border-stone-700/70'
              src={user?.avatar?.url || getPlaceholderAvatar(user?.email || user?.id || '')}
              alt=''
            />
            <div className='text-sm leading-none grid gap-0.5'>
              <span className='font-medium text-slate-900 dark:text-white'>{user?.name}</span>
              <span className='text-xs font-medium text-slate-700/70 dark:text-white/70'>{user?.email}</span>
            </div>
          </div>
          <div className='p-1'>
            {canReadProjects && <Suboption label={t('user.account')} onClick={_handleAccount} icon={<User />} />}
            {canReadProjects && user?.isReferrer && (
              <Suboption
                label={t('user.referrals')}
                onClick={() => {
                  setIsUserOpen(false);
                  navigate('/referrals');
                }}
                icon={<Gift className='size-4' />}
              />
            )}
            {/* <Suboption
              label='Billing'
              //onClick={_handleOpenSettings}
              icon={<CreditCard />}
            /> */}
            <Suboption
              label={t('user.theme')}
              //onClick={_handleOpenSettings}
              icon={<Moon />}
              suboptions={[
                {
                  icon: <Sun />,
                  label: t('user.themeLight'),
                  checked: theme === 'LIGHT',
                  onClick: () => {
                    setTheme('LIGHT' as const);
                  },
                },
                {
                  icon: <Moon />,
                  label: t('user.themeDark'),
                  checked: theme === 'DARK',
                  onClick: () => {
                    setTheme('DARK' as const);
                  },
                },
                {
                  icon: <Desktop />,
                  label: t('user.themeSystem'),
                  checked: !theme || theme === 'SYSTEM',
                  onClick: () => {
                    setTheme('SYSTEM' as const);
                  },
                },
              ]}
            />
            {/* <Suboption
              label='Notifications'
              //onClick={_handleOpenSettings}
              icon={<Bell />}
            /> */}
            <div className='border-b border-slate-200/70 dark:border-stone-800 rounded-full my-1' />
            <Suboption onClick={_handleLogout} label={t('user.logOut')} icon={<Logout />} destructive />
          </div>
          {/* <ReleaseInfo /> */}
        </div>
        <div
          onClick={() => setIsUserOpen(true)}
          className={cn('flex px-4 pb-4 pt-2 gap-2 relative group cursor-pointer', isCollapsed && 'px-2 pb-2')}
        >
          <img
            className={cn(
              'size-9 object-cover rounded-lg border border-slate-200/70 dark:border-stone-800',
              isCollapsed && 'size-7',
            )}
            src={user?.avatar?.url || getPlaceholderAvatar(user?.email || user?.id || '')}
            alt=''
          />
          {!isCollapsed && (
            <div className='text-sm leading-none flex flex-col gap-0.5 justify-center'>
              <span className='font-medium text-slate-900 dark:text-white'>{user?.name}</span>
              <span className='text-xs font-medium text-slate-700/70 dark:text-white/70'>{user?.email}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ value, max, tooltip }: { value: number; max: number; tooltip: string | JSX.Element }) => {
  const id = useId();
  const val = value / max;
  return (
    <>
      <div data-tooltip-id={id} className='h-1.5 w-8 bg-slate-200/70 dark:bg-stone-800/70 rounded-full overflow-hidden'>
        <div
          className={cn(
            'h-full bg-slate-500/70 dark:bg-stone-200/70 rounded-full',
            val >= 0.8 && 'bg-rose-500 dark:bg-rose-500/70',
            val < 0.4 && 'bg-green-500 dark:bg-green-500/70',
          )}
          style={{ width: `${val * 100}%` }}
        />
      </div>
      <PortalTooltip id={id}>
        <div className=''>
          {typeof tooltip === 'string'
            ? tooltip.replace('{{percent}}', `${Math.round((10000 * value) / max) / 100}%`)
            : tooltip}
        </div>
      </PortalTooltip>
    </>
  );
};

export default Sidebar;
